import { useId, useState, type FormEvent } from 'react';
import { useSWRConfig } from 'swr';

import {
  INCOME_SOURCE_TYPES,
  STABILITY_CLASSES,
  validateIncomeSourceInput,
  type IncomeSourceType,
  type StabilityClass,
  type ValidationIssue,
} from '@pfo/domain';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { resolveCategoryEmoji } from '../../../shared/ui/icons/categoryEmoji.js';
import { VALIDATION_MESSAGES } from '../../../shared/lib/validationMessages.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useAccounts } from '../../accounts/hooks/useAccounts.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { toggleFavorite, updatePreferences } from '../../preferences/data/preferencesRepository.js';
import { PREFERENCES_KEY, usePreferences } from '../../preferences/hooks/usePreferences.js';
import {
  createIncomeSource,
  updateIncomeSource,
  type IncomeSource,
} from '../data/incomeSourcesRepository.js';
import { useIncomeSources } from '../hooks/useIncomeSources.js';
import { INCOME_SOURCE_TYPE_LABELS, STABILITY_CLASS_LABELS } from '../model/incomeLabels.js';
import styles from './IncomeSourcesPage.module.css';

/**
 * "Nguồn thu nhập" — salary, tax refund, anything money arrives from (plan step 1.1).
 *
 * Two things on this screen are financial inputs rather than labels: how stable a source is
 * (it decides what `safe_to_invest` may count — B10) and which account it lands in (the
 * default Quick Add picks in step 1.2).
 */
export function IncomeSourcesPage() {
  const contextState = useCurrentContext();
  const sourcesState = useIncomeSources();
  const accountsState = useAccounts();
  const [isFormOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (contextState.status === 'loading' || sourcesState.status === 'loading') {
    return <LoadingState label="Đang tải nguồn thu…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (sourcesState.status === 'error') {
    return <ErrorState message={sourcesState.message} onRetry={sourcesState.retry} />;
  }

  const accounts =
    accountsState.status === 'ready'
      ? accountsState.overview.accounts.filter((account) => account.isActive)
      : [];
  const active = sourcesState.sources.filter((source) => source.isActive);
  const archived = sourcesState.sources.filter((source) => !source.isActive);

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Nguồn thu nhập</h1>
        {!isFormOpen ? (
          <button type="button" className={styles.addButton} onClick={() => setFormOpen(true)}>
            + Thêm nguồn thu
          </button>
        ) : null}
      </div>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}

      {isFormOpen ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>Nguồn thu mới</h2>
          <IncomeSourceForm
            context={{
              householdId: contextState.context.household.id,
              subjectId: contextState.context.selfSubject.id,
            }}
            accounts={accounts.map((account) => ({ id: account.id, name: account.name }))}
            onCancel={() => setFormOpen(false)}
            onCreated={() => {
              setFormOpen(false);
              setNotice('Đã thêm nguồn thu.');
              void sourcesState.refresh();
            }}
          />
        </section>
      ) : null}

      {sourcesState.sources.length === 0 ? (
        <EmptyState
          icon="💰"
          title="Chưa có nguồn thu nào"
          message="Thêm lương và các khoản thu khác để ghi nhận tiền vào đúng chỗ."
        />
      ) : (
        <>
          <SourceList
            title="Đang dùng"
            sources={active}
            onChanged={() => void sourcesState.refresh()}
          />
          <SourceList
            title="Đã ẩn"
            sources={archived}
            onChanged={() => void sourcesState.refresh()}
          />
        </>
      )}
    </div>
  );
}

function SourceList({
  title,
  sources,
  onChanged,
}: {
  title: string;
  sources: readonly IncomeSource[];
  onChanged: () => void;
}) {
  const { mutate } = useSWRConfig();
  const preferencesState = usePreferences();
  const [message, setMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (sources.length === 0) return null;

  async function run(id: string, action: () => Promise<void>) {
    setBusyId(id);
    setMessage(null);
    try {
      await action();
      onChanged();
      await mutate(PREFERENCES_KEY);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>{title}</h2>
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}
      <ul className={lists.list}>
        {sources.map((source) => (
          <li key={source.id}>
            <div className={cx(styles.row, !source.isActive && styles.rowMuted)}>
              {/* The same emoji Quick Add and the dashboard draw for this source, so one
                  source looks like itself everywhere (N2). */}
              <span className={styles.rowIcon} aria-hidden="true">
                {resolveCategoryEmoji({ icon: null, name: source.name })}
              </span>

              <span className={styles.rowText}>
                <span className={styles.rowName}>{source.name}</span>
                <span className={styles.rowMeta}>
                  {INCOME_SOURCE_TYPE_LABELS[source.sourceType]} ·{' '}
                  {STABILITY_CLASS_LABELS[source.stabilityClass]}
                </span>
              </span>

              {/* Quiet actions, still 44px to the finger: two bordered labelled buttons per
                  row wrapped onto three lines and buried the names (PO, 2026-08-19) — the
                  same fix the category rows already got. */}
              <span className={styles.rowActions}>
                {preferencesState.status === 'ready' ? (
                  <button
                    type="button"
                    className={cx(styles.iconButton, source.isFavorite && styles.iconButtonActive)}
                    disabled={busyId === source.id}
                    aria-pressed={source.isFavorite}
                    aria-label={
                      source.isFavorite
                        ? `Bỏ yêu thích ${source.name}`
                        : `Đánh dấu yêu thích ${source.name}`
                    }
                    // Favourites are a personal preference, not household data, so this
                    // writes only the caller's own row.
                    onClick={() =>
                      void run(source.id, () =>
                        updatePreferences({
                          favoriteIncomeSourceIds: toggleFavorite(
                            preferencesState.preferences.favoriteIncomeSourceIds,
                            source.id,
                          ),
                        }),
                      )
                    }
                  >
                    {source.isFavorite ? '★' : '☆'}
                  </button>
                ) : null}

                {/* Hiding is reversible and keeps every entry ever posted, so it reads as a
                    quiet action, not a red warning. */}
                <button
                  type="button"
                  className={styles.textButton}
                  disabled={busyId === source.id}
                  aria-label={`${source.isActive ? 'Ẩn' : 'Dùng lại'} ${source.name}`}
                  onClick={() =>
                    void run(source.id, () =>
                      updateIncomeSource(source.id, { isActive: !source.isActive }),
                    )
                  }
                >
                  {source.isActive ? 'Ẩn' : 'Dùng lại'}
                </button>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function IncomeSourceForm({
  context,
  accounts,
  onCreated,
  onCancel,
}: {
  context: { householdId: string; subjectId: string };
  accounts: ReadonlyArray<{ id: string; name: string }>;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const typeId = useId();
  const stabilityId = useId();
  const destinationId = useId();

  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<IncomeSourceType>('salary');
  const [stabilityClass, setStabilityClass] = useState<StabilityClass>('stable');
  const [destination, setDestination] = useState('');
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const issueOf = (field: string) => issues.find((issue) => issue.field === field);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);

    const validation = validateIncomeSourceInput({ name, sourceType, stabilityClass });
    if (!validation.ok) {
      setIssues(validation.issues);
      return;
    }
    setIssues([]);

    setSaving(true);
    try {
      await createIncomeSource(context, {
        name,
        sourceType,
        stabilityClass,
        ...(destination === '' ? {} : { defaultDestinationAccountId: destination }),
      });
      onCreated();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={forms.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
      <div className={forms.field}>
        <label htmlFor={nameId}>Tên nguồn thu</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          placeholder="Ví dụ: Lương"
          autoComplete="off"
          enterKeyHint="next"
          aria-invalid={issueOf('name') !== undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {issueOf('name') ? (
          <p className={forms.fieldError}>{VALIDATION_MESSAGES[issueOf('name')!.code]}</p>
        ) : null}
      </div>

      <div className={forms.field}>
        <label htmlFor={typeId}>Loại</label>
        <select
          id={typeId}
          className={forms.input}
          value={sourceType}
          onChange={(event) => setSourceType(event.target.value as IncomeSourceType)}
        >
          {INCOME_SOURCE_TYPES.map((type) => (
            <option key={type} value={type}>
              {INCOME_SOURCE_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </div>

      <div className={forms.field}>
        <label htmlFor={stabilityId}>Mức độ ổn định</label>
        <select
          id={stabilityId}
          className={forms.input}
          value={stabilityClass}
          onChange={(event) => setStabilityClass(event.target.value as StabilityClass)}
        >
          {STABILITY_CLASSES.map((stability) => (
            <option key={stability} value={stability}>
              {STABILITY_CLASS_LABELS[stability]}
            </option>
          ))}
        </select>
        <p className={forms.hint}>
          Chỉ khoản thu ổn định được dùng làm cơ sở tính số tiền có thể đầu tư an toàn.
        </p>
      </div>

      <div className={forms.field}>
        <label htmlFor={destinationId}>Thường về tài khoản</label>
        <select
          id={destinationId}
          className={forms.input}
          value={destination}
          onChange={(event) => setDestination(event.target.value)}
        >
          <option value="">Chưa chọn</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>
      </div>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={forms.actions}>
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button type="submit" className={forms.primary} disabled={isSaving}>
          {isSaving ? 'Đang lưu…' : 'Lưu nguồn thu'}
        </button>
      </div>
    </form>
  );
}
